import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { fetchOrders } from "../services/orderService";
import { fetchReturnRequests } from "../services/returnService";
import { Order, ReturnRequest } from "../types/store";
import "../styles/pages/OrdersPage.css";

const ORDER_STEPS = [
  { key: "CONFIRMED", label: "Order confirmed" },
  { key: "PROCESSING", label: "Processing" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "OUT_FOR_DELIVERY", label: "Out for delivery" },
  { key: "DELIVERED", label: "Delivered" },
] as const;

const RETURN_STEPS = [
  { key: "CONFIRMED", label: "Return requested" },
  { key: "APPROVED", label: "Approved" },
  { key: "PICKUP_SCHEDULED", label: "Pickup scheduled" },
  { key: "PICKED_UP", label: "Picked up" },
  { key: "REFUNDED", label: "Refunded" },
] as const;

const REPLACEMENT_STEPS = [
  { key: "UNDER_REVIEW", label: "Replacement requested" },
  { key: "APPROVED", label: "Approved" },
  { key: "SHIPPED", label: "Replacement shipped" },
  { key: "DELIVERED", label: "Replacement delivered" },
] as const;

const formatStatus = (value: string) =>
  value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatLongDate = (value: string) =>
  new Date(value).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const getLatestReturnRequest = (returnRequests: ReturnRequest[], orderId: number) =>
  returnRequests
    .filter((request) => request.orderId === orderId)
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];

const OrderUpdatesPage: React.FC = () => {
  const { orderId } = useParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [returnRequests, setReturnRequests] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [ordersResponse, returnResponse] = await Promise.all([fetchOrders(), fetchReturnRequests()]);
        setOrders(ordersResponse);
        setReturnRequests(returnResponse);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, []);

  const order = useMemo(
    () => orders.find((entry) => entry.id === Number(orderId) || String(entry.id) === orderId) ?? null,
    [orderId, orders],
  );

  if (loading) {
    return (
      <section className="shell section page-section orders-page order-updates-page">
        <div className="store-card empty-state">
          <h1>Loading updates...</h1>
        </div>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="shell section page-section orders-page order-updates-page">
        <div className="store-card empty-state">
          <h1>Order not found</h1>
          <Link className="button" to="/orders">
            Back to orders
          </Link>
        </div>
      </section>
    );
  }

  const returnRequest = getLatestReturnRequest(returnRequests, order.id);
  const primaryItem = order.items[0];
  const steps = returnRequest
    ? returnRequest.requestType === "REPLACEMENT"
      ? REPLACEMENT_STEPS
      : RETURN_STEPS
    : order.status === "CANCELLED"
      ? [{ key: "CONFIRMED", label: "Order confirmed" }, { key: "CANCELLED", label: "Cancelled" }]
      : ORDER_STEPS;
  const currentStatus = returnRequest ? returnRequest.status : order.status;
  const currentIndex = steps.findIndex((step) => step.key === currentStatus);
  const activeIndex = currentIndex >= 0 ? currentIndex : 0;

  return (
    <section className="shell section page-section orders-page order-updates-page">
      <MobilePageHeader title="Order Updates" />

      <div className="order-updates-card">
        <div className="order-updates-card__product">
          {primaryItem ? <img src={primaryItem.image} alt={primaryItem.productName} /> : null}
          <div>
            <h1>{primaryItem ? primaryItem.productName : `Order ${order.orderNumber}`}</h1>
            <span>Order #{order.orderNumber}</span>
          </div>
        </div>

        <div className="order-updates-timeline">
          {steps.map((step, index) => {
            const isComplete = index <= activeIndex;
            const isCurrent = index === activeIndex;

            return (
              <div
                key={step.key}
                className={`order-updates-timeline__step ${isComplete ? "is-complete" : ""} ${isCurrent ? "is-current" : ""}`}
              >
                <span className="order-updates-timeline__dot" aria-hidden="true" />
                <span className="order-updates-timeline__rail" aria-hidden="true" />
                <div>
                  <strong>{step.label}</strong>
                  <p>
                    {index === 0
                      ? formatLongDate(returnRequest?.createdAt ?? order.createdAt)
                      : isCurrent
                        ? formatStatus(currentStatus)
                        : isComplete
                          ? "Completed"
                          : "Pending"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <Link className="order-updates-card__back" to={`/orders/${order.id}`}>
          Back to order details
        </Link>
      </div>
    </section>
  );
};

export default OrderUpdatesPage;
